import { type CoborrowerRequiredConfig } from 'config/borrowerFieldRequired.util'
import { ISelectOption } from 'stories/components/SelectOptionInputs/types'

import { InputType } from './input.type.constants'

export enum FieldOrigin {
  LoanStructure = 'LoanStructure',
  BorrowerInformation = 'BorrowerInformation',
  PropertyInformation = 'PropertyInformation',
  AdditionalPropertyAddresses = 'AdditionalPropertyAddresses',
  EmploymentAndIncome = 'EmploymentAndIncome',
  CustomInformation = 'CustomInformation',
  AssetLiability = 'AssetLiability',
  TrackRecord = 'TrackRecord',
  DeclarationsHMDA = 'DeclarationsHMDA',
  AdditionalMember = 'additionalMember',
  Other = 'Other',
  Custom = 'Custom',
  ManageAccount = 'ManageAccount',
}

export const isCustomOrigin = (fieldOrigin: FieldOrigin | string) =>
  fieldOrigin.startsWith(FieldOrigin.CustomInformation)

export const fieldOriginOptions: Record<FieldOrigin, string> = {
  [FieldOrigin.LoanStructure]: 'Loan Structure',
  [FieldOrigin.BorrowerInformation]: 'Borrower Information',
  [FieldOrigin.PropertyInformation]: 'Loan and Property Information',
  [FieldOrigin.AdditionalPropertyAddresses]: 'Additional Property Addresses',
  [FieldOrigin.EmploymentAndIncome]: 'Employment and Income',
  [FieldOrigin.CustomInformation]: 'Custom Information',
  [FieldOrigin.AssetLiability]: 'Asset & Liability',
  [FieldOrigin.TrackRecord]: 'Track Record',
  [FieldOrigin.AdditionalMember]: 'Additional Entity Members',
  [FieldOrigin.DeclarationsHMDA]: 'Declarations & HMDA',
  [FieldOrigin.Other]: 'Other',
  [FieldOrigin.Custom]: 'Custom',
  [FieldOrigin.ManageAccount]: 'Manage Account',
}

/**
 * Canonical order of all built-in (non-custom) field origins.
 * Add new built-in origins here in the desired position — initialization
 * and order-insertion are both derived from this list automatically.
 */
export const builtInOrderedOrigins: string[] = [
  FieldOrigin.BorrowerInformation,
  FieldOrigin.PropertyInformation,
  FieldOrigin.AdditionalPropertyAddresses,
  FieldOrigin.EmploymentAndIncome,
  FieldOrigin.AssetLiability,
  FieldOrigin.TrackRecord,
  FieldOrigin.DeclarationsHMDA,
  'sign',
  'credit',
  FieldOrigin.AdditionalMember,
  'mlds',
]

export const fieldOriginForVisibility = [FieldOrigin.ManageAccount]

/**
 * Synthetic origin for per-choice visible logic on loan-level attributes
 * (channel today; more fields can be added later). Not a loan-application page.
 */
export const LOAN_CONTEXT_VISIBLE_LOGIC_ORIGIN = 'LoanContext'
export const CHANNEL_VISIBLE_LOGIC_FIELD_KEY = 'channel'

/** @deprecated Use LOAN_CONTEXT_VISIBLE_LOGIC_ORIGIN — kept so any early 'Channel' saves still evaluate. */
export const CHANNEL_VISIBLE_LOGIC_ORIGIN = LOAN_CONTEXT_VISIBLE_LOGIC_ORIGIN

/** True when a Channel clause does not restrict (missing or empty selection = all channels). */
export const isUnrestrictedChannelVisibleLogic = (logic: {
  fieldOrigin?: string
  fieldKey?: string
  value?: unknown
}) => {
  const origin = logic.fieldOrigin
  if (origin !== LOAN_CONTEXT_VISIBLE_LOGIC_ORIGIN && origin !== 'Channel') return false
  if (logic.fieldKey && logic.fieldKey !== CHANNEL_VISIBLE_LOGIC_FIELD_KEY) return false
  const v = logic.value
  if (v === undefined || v === null || v === '') return true
  if (Array.isArray(v) && !v.length) return true
  return false
}

export enum Operator {
  Equal = 0,
  NotEqual = 1,
  GreaterThan = 2,
  LessThan = 3,
}

export interface IVisibleLogic {
  fieldOrigin: FieldOrigin
  fieldKey: string
  value: string | number | boolean | string[]
  operator?: Operator
}

export interface ILoanFieldPos {
  fieldOrigin: FieldOrigin
  fieldKey: string
  [x: string]: any
}

export interface ISelectRangeOption {
  title: string
  from: number
  to?: number
  chapter?: number
  days?: number
  months?: number
  visible?: boolean
  visibleLogic?: IVisibleLogic[][]
  LLPA?: number
  LLRA?: number
}

export interface ISelectValueOption {
  title: string
  value: number
  subValue?: string
  interestOption?: number
  monthsInterest?: number
  visible?: boolean
  visibleLogic?: IVisibleLogic[][]
  LLPA?: number
  LLRA?: number
}

export interface ITitleLogicRule {
  title: string
  visibleLogic: IVisibleLogic[][]
}

export interface IViewOnlyLogicCondition {
  productTypes?: Record<string, boolean>
  loanStatus?: Record<string, boolean>
  accountTypes?: Record<string, boolean>
}

export interface ILLPALLRA {
  key?: string
  title: string
  from?: number
  to?: number
  LLPA?: number
  LLRA?: number
}

export interface IVisibleProp {
  title?: string
  titleLogic?: ITitleLogicRule[]
  inputType?: string
  type?: string
  section?: string
  span?: number | string
  tooltip?: string
  defaultValue?: any
  visible?: boolean
  required?: boolean
  /** Per-borrower “required” for non-primary positions; may be legacy `boolean` for all co-borrowers. */
  coborrowerRequired?: CoborrowerRequiredConfig
  visibleLogic?: IVisibleLogic[][]
  visibleAccountTypes?: Record<string, boolean>
  visibleLoanTypes?: Record<string, boolean>
  options?: ISelectOption[] | ISelectRangeOption[] | ISelectValueOption[]
  llpaLLRAs?: ILLPALLRA[]
  linkToParty?: { isActive: boolean; type: string; input: string }
  linkedField?: { fieldOrigin?: FieldOrigin; fieldKey?: string; option0?: string; option1?: string }
  hasDefaultOption?: boolean

  lockByAccountTypes?: Record<string, boolean>
  lockByLoanStatus?: Record<string, boolean>
  viewOnlyLogic?: IViewOnlyLogicCondition[]
  /** Selected account types may edit; explicit `false` is view-only. Missing/empty = all can edit. */
  editPermissionAccountTypes?: Record<string, boolean>

  byteproKey?: string
}

export interface IVisiblePropActionButton extends IVisibleProp {
  actionType: string
  actionKey: string
  buttonStyle?: 'primary' | 'outline' | 'link'
  icon?: string
  clickMode?: 'once' | 'always'
}

export interface ILoanOriginSection {
  id: number
  title?: string
  fields: string[]
}

export type LoanFieldPropsProperties = Record<
  FieldOrigin,
  {
    sectionType?: string
    accountTypes: Record<string, boolean>
    visibleLoanTypes: Record<string, boolean>
    visibleByBorrower: Record<string, boolean>
    visibleByCondition: Record<string, boolean>
    visibleLogicByAccountTypes?: { accountTypes: string[]; visibleLogic: IVisibleLogic[][] }[]
    visibleLogic?: IVisibleLogic[][]
    lockByAccountTypes?: Record<string, boolean>
    lockByLoanStatus?: Record<string, boolean>
    additionalInputs?: any
    numberOfGrid?: number | string
  }
>

export type LoanFieldProps = {
  // [P in FieldOrigin]: Record<string, IVisibleProp>
  [x: string]: Record<string, IVisibleProp>
} & {
  sections?: {
    [T in FieldOrigin]?: ILoanOriginSection[]
  }
  order?: FieldOrigin[]
  customTitle?: Record<FieldOrigin, string>
  visibility?: Record<FieldOrigin, string>
  properties?: LoanFieldPropsProperties
}

export const findLinkedFieldKeys = (
  fields: Record<string, IVisibleProp>,
  fieldOrigin: FieldOrigin,
  fieldKey: string,
) => {
  return Object.keys(fields).filter((key) => {
    const { linkedField } = fields[key]
    return linkedField?.fieldOrigin == fieldOrigin && linkedField?.fieldKey == fieldKey
  })
}

export const NewInputTypeOptions = {
  Text: 'Text',
  ToggleButton: 'Toggle Button',
  Select: 'Select',
  TextArea: 'TextArea',
  FileTable: 'FileTable',
  CheckBox: 'CheckBox',
  ActionButton: 'Action Button',
  Label: 'Label',
  Custom: 'Custom',
  GoogleMap: 'Google Map',
}

/** Stored `type` for FileTable: '' means ordinary; extend with more keys as needed. */
export const NewFileTableTypeOptions: Record<string, string> = {
  '': 'Ordinary',
  fannie34Xml: 'Fannie 3.4 XML',
}

export const NewInputTextOptions = {
  thousandSep: 'Number with Thousand Separator',
  thousandSepNoDecimal: 'Number with Thousand Separator without Decimal',
  date: 'Date',
  time: 'Time',
  email: 'Email',
  phone: 'Phone',
  number: 'Number',
  password: 'Password',
  text: 'Text',
  ssn: 'SSN',
  mers: 'MERS',
  creditScore: 'Credit Score',
  entityTaxID: 'Tax ID', // Default is 'text'
  color: 'Color',
  dollar: 'Dollar',
  percentage: 'Percentage',
  percentOfLoanAmount: '% of Loan Amount Added',
}

export const isInputToTriggerBlur = (input: InputType) => {
  return ['FileTable', 'ActionButton'].includes(input?.inputType)
}
